import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { APP_ICONS } from '../../../icons/icon-provider';
import { DashboardService } from '../../../services/dashboard.service';
import { SkeletonListComponent } from '../../../shared/ui/skeleton/skeleton-list.component';
import { DashIconComponent } from '../shared/dash-icon.component';
import { TimezoneDatePipe } from '../../../timezone-date.pipe';
import {
  FunnelRow,
  SlotsBlock,
  SpotlightRow,
  SpotlightVariant,
  ThisWeekView,
  deltaFromChange,
  formatLongDate,
  getgreeting,
  mapActiveJobRow,
  mapJobCandidateSlots,
  mapOfferStages,
  mapRecruiterFunnel,
  mapRecruiterOfferRow,
  mapSpotlightCandidate,
  mapThisWeekSummary,
  mapTodayActivity,
  mapWeeklyApplicants,
  mapWeeklyMetrics,
} from '../shared/dashboard-mappers';
import { RECRUITER_MOCK } from './mock-data';
import { JobCreationTriggerService } from '../../../services/job-creation-trigger.service';
import { SlidePanelService } from '../../../services/slide-panel.service';
import { GigsumoConstants } from '../../../services/GigsumoConstants';
import { JobService } from '../../../services/job.service';
// Hidden <app-candidate-card> exposes the plan-gated invite() + its own
// #jobsupplytemplate, so the Talent Pool spotlight Invite reuses the exact
// modal flow the Talent Pool listing uses — mirroring how the Bench Sales
// dashboard drives Marketplace "Apply" through a hidden <app-job-card>.
import { CandidateCardComponent } from 'src/app/components/cards/candidate-card/candidate-card.component';
import { CompactCountPipe } from '../../../pipe/compact-count.pipe';

@Component({
  selector: 'app-recruiter-dashboard',
  standalone: true,
  imports: [CommonModule, NgIconComponent, DashIconComponent, SkeletonListComponent, TimezoneDatePipe, CandidateCardComponent, CompactCountPipe],
  providers: [provideIcons(APP_ICONS)],
  templateUrl: './recruiter-dashboard.component.html',
  styleUrls: ['./recruiter-dashboard.component.scss']
})
export class RecruiterDashboardComponent implements OnInit, OnDestroy {
  /** Mock still backs Quick Actions + AI Insights (kept per product). Everything else is live. */
  readonly data = RECRUITER_MOCK;

  // ── Welcome banner (live) ─────────────────────────────────────────
  // Seed from localStorage (persisted at login) so the greeting name paints
  // instantly; fetchCurrentUser() then refreshes it from the API.
  userFirstName = (localStorage.getItem('firstName') || '').trim();
  // readonly todayFormatted = formatLongDate();

  currentTz = localStorage.getItem('timezonedate') || '';
 
  get todayFormatted(): string {
    return formatLongDate(new Date(), this.currentTz);
  }
  
  get greeting(): string {
    return getgreeting(new Date(), this.currentTz);
  }

  /** True when the Applicants-This-Week mini-chart has at least one non-zero bar.
   *  Reads the live override first (set by `fetchJobPipelineStats`), then falls
   *  back to the mock seed. */
  get hasApplicantsThisWeek(): boolean {
    const rows = this.applicantsThisWeekRows ?? this.data.applicantsThisWeek ?? [];
    return rows.some((b: any) => (b?.val ?? 0) > 0);
  }

  /** Bar height (%) for the Applicants-This-Week chart, scaled so the busiest
   *  day fills the track and the rest grow in proportion. Without this the old
   *  fixed `val * 1.6px` scale left small counts flat and clipped large ones at
   *  the container ceiling, so bars never reflected their values. Zero-value
   *  days fall back to the CSS `min-height` stub. */
  applicantBarHeight(val: number): number {
    const rows = this.applicantsThisWeekRows ?? this.data.applicantsThisWeek ?? [];
    const max = Math.max(0, ...rows.map((b: any) => Number(b?.val ?? 0)));
    if (max <= 0) return 0;
    return (Number(val ?? 0) / max) * 100;
  }

  // ── Job & Candidate Slots (live from /dashboard/job-candidate-slots) ─
  slots: SlotsBlock = { used: 0, open: 0, allocated: 0, totalPosted: 0, plan: '' };
  loadingSlots = false;

  // ── This Week summary (live from /dashboard/this-week-summary) ─────
  thisWeek: ThisWeekView = { range: '', rows: [] };
  loadingThisWeek = false;

  // ── Active Jobs list (live from jobs/filter, paginated) ───────────
  activeJobs: any[] = [];
  activeJobsCursor: any[] | null = null;
  activeJobsHasMore = false;
  activeJobsTotalCount = 0;
  loadingActiveJobs = false;
  loadingMoreActiveJobs = false;

  // ── Weekly Activity Metrics (live) ─────────────────────────────────
  weeklyMetrics: { range: string; goals: any[] } | null = null;
  loadingWeekly = false;

  // ── Offer Pipeline (live, paginated via "Load more") ──────────────
  offerStages: any[] = [];
  offers: any[] = [];
  offersCursor: any[] | null = null;
  offersHasMore = false;
  offersTotalCount = 0;
  loadingOffers = false;
  loadingMoreOffers = false;

  // ── Job Pipeline Stats (business-scope) ───────────────────────────
  /** Keyed by KPI label; template falls back to mock value when absent. */
  kpiOverrides: Record<string, string | number> = {};
  /** Live trend pill per KPI tile. Populated from This Week Summary
   *  v1.0.252 response:
   *    - `activeJobsStats` → week-over-week trend on Active Jobs
   *    - `todayApplicants` → day-over-day trend on Total Applicants
   *  Template prefers this over mock `kpi.trend` when keyed present. */
  kpiTrendOverrides: Record<string, { text: string; dir: 'up' | 'down' | 'flat' }> = {};
  /** Live funnel; null → template falls back to mock `data.pipeline`. */
  pipelineRows: FunnelRow[] | null = null;
  /** Live "Applicants This Week" bars from jobs/getBusinessJobPipelineStats
   *  `weeklyApplicants` (added 2026-04-25). null → template falls back to
   *  mock `data.applicantsThisWeek`. */
  applicantsThisWeekRows: { day: string; val: number; active: boolean }[] | null = null;
  loadingPipelineStats = false;

  // ── Today's Activity (business-scope, paginated via "Load more") ──
  todayActivity: any[] = [];
  todayActivityCursor: any[] | null = null;
  todayActivityHasMore = false;
  todayActivityTotalCount = 0;
  loadingTodayActivity = false;
  loadingMoreTodayActivity = false;

  // ── Top Candidate Spotlight (driven by whichever Active Job is selected) ──
  activeSpotlightTab: SpotlightVariant = 'applicants';
  selectedJobId: string | null = null;
  selectedJobTitle = '';
  /** Raw status of the selected Active Job (from mapActiveJobRow) — BLOCKED
   *  disables the Spotlight Invite action. */
  selectedJobStatus = '';

  private readonly SPOTLIGHT_PAGE_SIZE = 5;

  spotlightApplicants: SpotlightRow[] = [];
  spotlightTalent:     SpotlightRow[] = [];
  spotlightMarket:     SpotlightRow[] = [];
  spotlightApplicantsTotal = 0;
  spotlightTalentTotal     = 0;
  spotlightMarketTotal     = 0;
  loadingSpotlightApplicants = false;
  loadingSpotlightTalent     = false;
  loadingSpotlightMarket     = false;
  loadingMoreSpotlightApplicants = false;
  loadingMoreSpotlightTalent     = false;
  loadingMoreSpotlightMarket     = false;

  // Applicants: offset-based pagination.
  private spotlightApplicantsPage = 0;
  spotlightApplicantsHasMore = false;

  // Talent + Marketplace: cursor-based pagination via `searchAfterKey`.
  private spotlightTalentCursor: any = null;
  private spotlightMarketCursor: any = null;
  spotlightTalentHasMore = false;
  spotlightMarketHasMore = false;

  get spotlightRows(): SpotlightRow[] {
    switch (this.activeSpotlightTab) {
      case 'applicants': return this.spotlightApplicants;
      case 'talent':     return this.spotlightTalent;
      default:           return this.spotlightMarket;
    }
  }
  get spotlightLoading(): boolean {
    switch (this.activeSpotlightTab) {
      case 'applicants': return this.loadingSpotlightApplicants;
      case 'talent':     return this.loadingSpotlightTalent;
      default:           return this.loadingSpotlightMarket;
    }
  }
  get spotlightHasMore(): boolean {
    switch (this.activeSpotlightTab) {
      case 'applicants': return this.spotlightApplicantsHasMore;
      case 'talent':     return this.spotlightTalentHasMore;
      default:           return this.spotlightMarketHasMore;
    }
  }
  get spotlightLoadingMore(): boolean {
    switch (this.activeSpotlightTab) {
      case 'applicants': return this.loadingMoreSpotlightApplicants;
      case 'talent':     return this.loadingMoreSpotlightTalent;
      default:           return this.loadingMoreSpotlightMarket;
    }
  }
  spotlightTabCount(id: SpotlightVariant): number {
    // Mirror the Active Jobs card on the left: each row already carries the
    // job's applicants count via `mapActiveJobRow().applied` (sourced from
    // `appliedCount` on the jobs/filter response). Reading it from the
    // selected row keeps the right-side Applicants badge in sync with the
    // left-side number — the spotlight applicants endpoint can paginate or
    // role-filter, so its total may not match what the left card shows.
    if (id === 'applicants') {
      const selected = this.activeJobs.find(j => j.id === this.selectedJobId);
      return selected?.applied ?? this.spotlightApplicantsTotal;
    }
    if (id === 'talent') return this.spotlightTalentTotal;
    return this.spotlightMarketTotal;
  }

  loadMoreSpotlight() {
    if (!this.selectedJobId || this.spotlightLoadingMore || !this.spotlightHasMore) return;
    switch (this.activeSpotlightTab) {
      case 'applicants': this.fetchSpotlightApplicants(this.selectedJobId, false); break;
      case 'talent':     this.fetchSpotlightTalent(this.selectedJobId, false);     break;
      case 'market':     this.fetchSpotlightMarket(this.selectedJobId, false);     break;
    }
  }

  selectSpotlight(id: SpotlightVariant) {
    this.activeSpotlightTab = id;
  }

  selectActiveJob(job: any) {
    if (!job?.id || job.id === this.selectedJobId) return;
    this.selectedJobId = job.id;
    this.selectedJobTitle = job.name || '';
    this.selectedJobStatus = job.status || '';
    // New job → invalidate all three caches and fire all three fetches
    // so the tab count badges reflect real totals immediately.
    this.spotlightApplicants = [];
    this.spotlightTalent     = [];
    this.spotlightMarket     = [];
    this.spotlightApplicantsTotal = 0;
    this.spotlightTalentTotal     = 0;
    this.spotlightMarketTotal     = 0;
    this.spotlightApplicantsPage  = 0;
    this.spotlightTalentCursor    = null;
    this.spotlightMarketCursor    = null;
    this.spotlightApplicantsHasMore = false;
    this.spotlightTalentHasMore     = false;
    this.spotlightMarketHasMore     = false;
    this.fetchSpotlightApplicants(job.id, true);
    this.fetchSpotlightTalent(job.id, true);
    this.fetchSpotlightMarket(job.id, true);
  }

  /** Some of the three Spotlight endpoints wrap the payload in `res.data`
   *  (applicants / marketplace) while talent-pool returns a flat body with
   *  `status: "SUCCESS"` at the top level. Normalize both shapes here. */
  private unwrapSpotlight(res: any): any | null {
    if (!res) return null;
    const ok =
      res.code === '00000' ||
      res.status === 'SUCCESS' ||
      Array.isArray(res.candidateList) ||
      Array.isArray(res.appliedCandidateList) ||
      (res.data && (Array.isArray(res.data.candidateList) || Array.isArray(res.data.appliedCandidateList)));
    if (!ok) return null;
    return res.data ?? res;
  }

  /** Defensive total-count extraction — the three APIs emit the total under
   *  slightly different keys. Tries the common variants in order of likelihood
   *  and falls back to the returned list length. */
  private pickTotal(data: any, list: any[]): number {
    if (data) {
      const cands = [
        data.totalCount,
        data.totalCandidatesCount,
        data.totalAppliedCandidates,
        data.totalAppliedCandidatesCount,
        data.totalApplicantsCount,
        data.totalMatchCount,
        data.totalElements,
        data.total,
      ];
      for (const v of cands) {
        if (v != null && v !== '' && !isNaN(Number(v))) return Number(v);
      }
    }
    return list.length;
  }

  private fetchSpotlightApplicants(jobId: string, reset: boolean) {
    if (reset) {
      this.loadingSpotlightApplicants = true;
      this.spotlightApplicantsPage = 0;
    } else {
      this.loadingMoreSpotlightApplicants = true;
    }
    const pageNumber = reset ? 0 : this.spotlightApplicantsPage;

    this.dash.findAppliedCandidates(jobId, this.SPOTLIGHT_PAGE_SIZE, pageNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (jobId !== this.selectedJobId) return;  // discard stale responses
          const body = this.unwrapSpotlight(res);
          if (body) {
            const list = body.appliedCandidateList || body.candidateList || [];
            const startRank = reset ? 0 : this.spotlightApplicants.length;
            const mapped = list.map((c: any, i: number) =>
              mapSpotlightCandidate(c, startRank + i + 1, 'applicants'));
            this.spotlightApplicants = reset ? mapped : [...this.spotlightApplicants, ...mapped];
            if (reset) this.spotlightApplicantsTotal = this.pickTotal(body, list);
            this.spotlightApplicantsPage = pageNumber + 1;
            this.spotlightApplicantsHasMore = this.spotlightApplicantsTotal > 0
              ? this.spotlightApplicants.length < this.spotlightApplicantsTotal
              : list.length === this.SPOTLIGHT_PAGE_SIZE;
          }
          if (reset) this.loadingSpotlightApplicants = false;
          else this.loadingMoreSpotlightApplicants = false;
        },
        error: () => {
          if (reset) this.loadingSpotlightApplicants = false;
          else this.loadingMoreSpotlightApplicants = false;
        },
      });
  }

  private fetchSpotlightTalent(jobId: string, reset: boolean) {
    if (reset) this.loadingSpotlightTalent = true;
    else this.loadingMoreSpotlightTalent = true;
    const cursor = reset ? null : this.spotlightTalentCursor;

    this.dash.findTalentPoolMatches(jobId, this.SPOTLIGHT_PAGE_SIZE, cursor)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (jobId !== this.selectedJobId) return;
          const body = this.unwrapSpotlight(res);
          if (body) {
            const list = body.candidateList || [];
            const startRank = reset ? 0 : this.spotlightTalent.length;
            const mapped = list.map((c: any, i: number) =>
              mapSpotlightCandidate(c, startRank + i + 1, 'talent'));
            this.spotlightTalent = reset ? mapped : [...this.spotlightTalent, ...mapped];
            if (reset) this.spotlightTalentTotal = this.pickTotal(body, list);
            this.spotlightTalentCursor = body.searchAfterKey ?? null;
            this.spotlightTalentHasMore = !!this.spotlightTalentCursor && list.length > 0;
          }
          if (reset) this.loadingSpotlightTalent = false;
          else this.loadingMoreSpotlightTalent = false;
        },
        error: () => {
          if (reset) this.loadingSpotlightTalent = false;
          else this.loadingMoreSpotlightTalent = false;
        },
      });
  }

  private fetchSpotlightMarket(jobId: string, reset: boolean) {
    if (reset) this.loadingSpotlightMarket = true;
    else this.loadingMoreSpotlightMarket = true;
    const cursor = reset ? null : this.spotlightMarketCursor;

    this.dash.findTopSuggestedCandidates(jobId, this.SPOTLIGHT_PAGE_SIZE, cursor)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (jobId !== this.selectedJobId) return;
          const body = this.unwrapSpotlight(res);
          if (body) {
            const list = body.candidateList || [];
            const startRank = reset ? 0 : this.spotlightMarket.length;
            const mapped = list.map((c: any, i: number) =>
              mapSpotlightCandidate(c, startRank + i + 1, 'market'));
            this.spotlightMarket = reset ? mapped : [...this.spotlightMarket, ...mapped];
            if (reset) this.spotlightMarketTotal = this.pickTotal(body, list);
            this.spotlightMarketCursor = body.searchAfterKey ?? null;
            this.spotlightMarketHasMore = !!this.spotlightMarketCursor && list.length > 0;
          }
          if (reset) this.loadingSpotlightMarket = false;
          else this.loadingMoreSpotlightMarket = false;
        },
        error: () => {
          if (reset) this.loadingSpotlightMarket = false;
          else this.loadingMoreSpotlightMarket = false;
        },
      });
  }

  private destroy$ = new Subject<void>();

  constructor(
    private dash: DashboardService,
    private router: Router,
    private jobCreationTrigger: JobCreationTriggerService,
    private slidePanel: SlidePanelService,
    private jobService: JobService,
  ) {}

  /** Avatar fallback colours for offers without a candidate photo — same
   *  name-hash palette the Candidates page uses, via the shared JobService. */
  getAvatarColor(name: string): string { return this.jobService.getColor(name || ''); }
  getAvatarTextColor(name: string): string { return this.jobService.getFullnameTextColor(name || ''); }

  // Delegates to the landing-page nav-bar's existing create-job flow (plan check,
  // org-setup gate, modal). Nav-bar is guaranteed mounted on /landingPage/recruiter.
  onPostNewJob(): void {
    this.jobCreationTrigger.trigger('JOBS');
  }

  goToPlanQuota() { this.router.navigate(['/plan-quota']); }

  goToAllJobs() {
    this.router.navigate(['/newjobs'], { queryParams: { source: 'myJobs' } });
  }

  /** Active Jobs card header. Super-admins manage the whole business in the
   *  Job Center — except on the Free plan, where super-admin scope doesn't
   *  apply, so everyone goes to their own My Jobs list. */
  goToActiveJobsList() {
    const isFreePlan = localStorage.getItem('MemberShipType') === GigsumoConstants.FREE_PLAN;
    if (!isFreePlan && localStorage.getItem('businessRole') === 'SUPERADMIN') {
      this.router.navigate(['/job-center']);
      return;
    }
    this.goToAllJobs();
  }

  /** Open the currently-selected job on the newjobs page with the tab that
   *  matches the active Spotlight tab pre-selected. */
  private readonly SPOTLIGHT_TAB_ROUTES: Record<SpotlightVariant, string> = {
    applicants: 'job-applicants',
    talent:     'talent-pool',
    market:     'suggested-candidates',
  };

  goToSpotlightTab() {
    if (!this.selectedJobId) return;
    this.router.navigate(['/newjobs'], {
      queryParams: {
        jobId: this.selectedJobId,
        tabName: this.SPOTLIGHT_TAB_ROUTES[this.activeSpotlightTab],
        source: 'myJobs',
      },
    });
  }

  /** Open the V3 candidate profile slider on the AI Insights tab for the
   *  clicked Spotlight row. Mirrors the Match-badge click on the listing
   *  pages (`candidate-card-table-view → openMatch`). Re-uses the existing
   *  pageFrom conventions from SPOTLIGHT_TAB_ROUTES — same three values
   *  `ai-criteria.util.ts:getRankingPayload()` understands. */
  openSpotlightMatch(row: SpotlightRow, ev?: Event) {
    ev?.stopPropagation();
    if (!row?.candidateId || !this.selectedJobId) return;
    this.slidePanel.openCandidateDetail(row.raw ?? { candidateId: row.candidateId }, this.selectedJobId, {
      variant: 'v3',
      initialTab: 'ai',
      pageFrom: this.SPOTLIGHT_TAB_ROUTES[this.activeSpotlightTab],
    });
  }

  /** Hidden `<app-candidate-card>` instance whose only purpose is to expose its
   *  `invite()` method + `#jobsupplytemplate` so the Talent Pool spotlight
   *  Invite reuses the listing's plan-gated invite modal. Parity with the
   *  Bench Sales dashboard's hidden `<app-job-card>` (spotlightJobCardRef). */
  @ViewChild('spotlightCandidateCardRef') spotlightCandidateCardRef: CandidateCardComponent;

  /** Talent Pool tab → Invite. Only wired for the Talent Pool tab; the
   *  Applicants (Review) and Marketplace (View) actions stay no-ops for now.
   *  `c.raw` is the same findTalentPoolMatches candidate record the Talent Pool
   *  listing feeds to invite(). */
  onSpotlightInvite(c: SpotlightRow) {
    if (this.activeSpotlightTab !== 'talent') return;
    if (this.selectedJobStatus === 'BLOCKED') return;
    if (!this.spotlightCandidateCardRef) return;
    this.spotlightCandidateCardRef.invite(
      c.raw,
      undefined,
      this.spotlightCandidateCardRef.jobsupplytemplate,
    );
  }

  ngOnInit() {
    // Welcome-banner name only needs `userId` (from localStorage), not the
    // primed business/org ids — fire it immediately and in parallel so the
    // greeting name isn't delayed behind the primeIds() round-trip.
    this.fetchCurrentUser();
    // Resolve the business + organisation id ONCE per load — fresh from the
    // backend — before firing the API family, so every dashboard payload that
    // needs it gets the current value from a single lookup (not one per
    // endpoint). See DashboardService.primeIds().
    this.dash.primeIds()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.fetchSlots();
        this.fetchThisWeek();
        this.fetchWeekly();
        this.fetchOffers(true);
        // Pipeline stats + Today's Activity now support USER context per
        // Dashboard_API_Documentation.html §2 — no longer gated on super-admin.
        this.fetchJobPipelineStats();
        this.fetchTodayActivity(true);
        // `getActiveJobs` now branches internally — super-admins get the
        // business-scoped `jobs/filter` payload; regular users get the
        // `/newjobs?source=myJobs` payload — so the gate isn't needed here.
        this.fetchActiveJobs(true);
      });
  }

  private fetchSlots() {
    this.loadingSlots = true;
    this.dash.getJobCandidateSlots('JOB')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          // API returns code "00000" today, but spec mentions "00" too — accept both.
          if (res?.code === '00000' || res?.code === '00' || res?.data) {
            this.slots = mapJobCandidateSlots(res);
          }
          this.loadingSlots = false;
        },
        error: () => { this.loadingSlots = false; },
      });
  }

  private fetchThisWeek() {
    this.loadingThisWeek = true;
    this.dash.getThisWeekSummary('RECRUITER')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.code === '00000' || res?.code === '00' || res?.data) {
            const v = mapThisWeekSummary(res, 'recruiter');
            if (v) this.thisWeek = v;

            // Live KPI trend pills from v1.0.252 fields.
            // Active Jobs  → `activeJobsStats.weekCount` + arrow from `change`
            // Total Applicants → `todayApplicants.dayCount` + arrow from `change`
            const summary = res?.data?.summary;
            const aj = summary?.activeJobsStats ?? summary?.activeJobs;
            const ta = summary?.todayApplicants;
            const nextTrends: Record<string, { text: string; dir: 'up' | 'down' | 'flat' }> = {};
            // Big-number tile values now come from this-week-summary too:
            //   Active Jobs      → activeJobsStats.total
            //   Total Applicants → todayApplicants.total
            const nextOverrides: Record<string, string | number> = {};
            if (aj) {
              // Direction drives color (.trend-up = green, .trend-down = red,
              // .trend-flat = gray); the absolute value is what the user
              // reads — sign is encoded in the arrow + pill color.
              const d = deltaFromChange(aj.change);
              nextTrends['Active Jobs'] = {
                text: `${Math.abs(Number(aj.change ?? 0))} this week`,
                dir: d.dir,
              };
              if (aj.total != null) nextOverrides['Active Jobs'] = aj.total;
            }
            if (ta) {
              const d = deltaFromChange(ta.change);
              nextTrends['Total Applicants'] = {
                text: `${Math.abs(Number(ta.change ?? 0))} today`,
                dir: d.dir,
              };
              if (ta.total != null) nextOverrides['Total Applicants'] = ta.total;
            }
            this.kpiTrendOverrides = { ...this.kpiTrendOverrides, ...nextTrends };
            if (Object.keys(nextOverrides).length) {
              this.kpiOverrides = { ...this.kpiOverrides, ...nextOverrides };
            }
          }
          this.loadingThisWeek = false;
        },
        error: () => { this.loadingThisWeek = false; },
      });
  }

private fetchCurrentUser() {
    this.dash.getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          // /user/{id} returns the user object directly; other endpoints wrap in { data }.
          // Be defensive — try every likely path.
          const name = res?.firstName ?? res?.data?.firstName ?? res?.data?.user?.firstName ?? '';
          const trimmed = String(name).trim();
          if (trimmed) {
            this.userFirstName = trimmed;
            // Keep localStorage fresh so subsequent visits paint the name
            // instantly, and cover login paths that didn't persist it.
            localStorage.setItem('firstName', trimmed);
          }
        },
        error: () => {},
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  donutBackground(): string {
    const used = this.slots.used;
    const total = this.slots.allocated;
    const deg = total > 0 ? (used / total) * 360 : 0;
    return `conic-gradient(var(--gs-purple-light) 0 ${deg}deg, var(--gs-gray-100) ${deg}deg 360deg)`;
  }

  /** stroke-dashoffset for the SVG ring donut "used" arc.
   *  Circumference of r=25 circle is 2π·25 ≈ 157.08. Hiding `dashoffset`
   *  worth of the stroke from the tail reveals `157 - dashoffset` of the
   *  stroke from the head. Head is at 12 o'clock due to the parent
   *  `transform: rotate(-90deg)`. */
  donutDashoffset(): number {
    const used = this.slots.used;
    const total = this.slots.allocated;
    if (total <= 0) return 157;
    const ratio = Math.max(0, Math.min(1, used / total));
    return 157 * (1 - ratio);
  }

  private fetchWeekly() {
    this.loadingWeekly = true;
    this.dash.recruiterWeeklyMetrics()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.code === '00000') {
            this.weeklyMetrics = mapWeeklyMetrics(res.data?.metrics);
          }
          this.loadingWeekly = false;
        },
        error: () => { this.loadingWeekly = false; },
      });
  }

  private fetchOffers(reset: boolean) {
    if (reset) this.loadingOffers = true;
    else this.loadingMoreOffers = true;

    this.dash.recruiterOfferPipeline({
      searchAfterKey: reset ? null : this.offersCursor,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.code === '00000') {
            const p = res.data?.pipeline;
            if (p) {
              this.offerStages = mapOfferStages(p.summary);
              const mapped = (p.offers || []).map(mapRecruiterOfferRow);
              this.offers = reset ? mapped : [...this.offers, ...mapped];
              this.offersCursor = p.searchAfterKey ?? null;
              this.offersHasMore = !!p.hasMore;
              if (reset && p.totalCount != null) this.offersTotalCount = p.totalCount;
            }
          }
          if (reset) this.loadingOffers = false;
          else this.loadingMoreOffers = false;
        },
        error: () => {
          if (reset) this.loadingOffers = false;
          else this.loadingMoreOffers = false;
        },
      });
  }

  loadMoreOffers() {
    if (this.offersHasMore && !this.loadingMoreOffers) this.fetchOffers(false);
  }

  private fetchJobPipelineStats() {
    this.loadingPipelineStats = true;
    this.dash.recruiterJobPipelineStats('ACTIVE')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          // 2026-04-25: backend now emits `code: 1` / `status: "SUCCESS"`.
          // Accept legacy `'00000'` too so the dashboard keeps working
          // through any rolling deploy.
          const ok = res?.code === '00000' || res?.code === 1 || res?.status === 'SUCCESS';
          if (ok && res.data) {
            const d = res.data;
            // NOTE: 'Active Jobs' / 'Total Applicants' tile numbers now come
            // from dashboard/this-week-summary (activeJobsStats.total /
            // todayApplicants.total) in fetchThisWeek(). This endpoint only
            // feeds the funnel + weekly-applicants chart now.
            this.pipelineRows = mapRecruiterFunnel(d);
            // New `weeklyApplicants` block (2026-04-25) feeds the mini-chart
            // under the funnel. mapWeeklyApplicants returns null on absent
            // payload, leaving the mock fallback visible.
            this.applicantsThisWeekRows = mapWeeklyApplicants(d.weeklyApplicants);
          }
          this.loadingPipelineStats = false;
        },
        error: () => { this.loadingPipelineStats = false; },
      });
  }

  private fetchTodayActivity(reset: boolean) {
    if (reset) this.loadingTodayActivity = true;
    else this.loadingMoreTodayActivity = true;

    this.dash.recruiterTodaysActivity({
      searchAfterKey: reset ? null : this.todayActivityCursor,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.code === '00000' && res.data) {
            const d = res.data;
            // viewSummary is only meaningful on the first page; after that it
            // would duplicate across "Load more" clicks.
            const mapped = mapTodayActivity(
              d.events,
              reset ? d.viewSummary : [],
              'recruiter'
            );
            this.todayActivity = reset ? mapped : [...this.todayActivity, ...mapped];
            this.todayActivityCursor = d.searchAfterKey ?? null;
            this.todayActivityHasMore = !!d.searchAfterKey && (d.events || []).length > 0;
            // Doc §4.3: `totalElements` removed; `eventCount` is now the
            // total across pages (constant, not current page size).
            if (reset && d.eventCount != null) this.todayActivityTotalCount = d.eventCount;
          }
          if (reset) this.loadingTodayActivity = false;
          else this.loadingMoreTodayActivity = false;
        },
        error: () => {
          if (reset) this.loadingTodayActivity = false;
          else this.loadingMoreTodayActivity = false;
        },
      });
  }

  loadMoreTodayActivity() {
    if (this.todayActivityHasMore && !this.loadingMoreTodayActivity) this.fetchTodayActivity(false);
  }

  private fetchActiveJobs(reset: boolean) {
    if (reset) this.loadingActiveJobs = true;
    else this.loadingMoreActiveJobs = true;

    this.dash.getActiveJobs({
      limit: 6,
      searchAfterKey: reset ? null : this.activeJobsCursor,
      status: ['ACTIVE'],
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.code === '00000' && res.data) {
            const d = res.data;
            const mapped = (d.jobList || []).map(mapActiveJobRow);
            this.activeJobs = reset ? mapped : [...this.activeJobs, ...mapped];
            this.activeJobsCursor = d.searchAfterKey ?? null;
            this.activeJobsHasMore = !!d.searchAfterKey && (d.jobList || []).length > 0;
            if (reset && d.totalJobsCount != null) this.activeJobsTotalCount = d.totalJobsCount;
            // Auto-select the first job so the Spotlight card has data immediately.
            if (reset && !this.selectedJobId && mapped.length) {
              this.selectActiveJob(mapped[0]);
            } else if (this.selectedJobId) {
              // Same job still selected after a refresh — re-sync its status
              // (selectActiveJob early-returns on an unchanged id, so a job
              // that was just blocked would otherwise keep its stale status).
              const cur = this.activeJobs.find(j => j.id === this.selectedJobId);
              if (cur) this.selectedJobStatus = cur.status || '';
            }
          }
          if (reset) this.loadingActiveJobs = false;
          else this.loadingMoreActiveJobs = false;
        },
        error: () => {
          if (reset) this.loadingActiveJobs = false;
          else this.loadingMoreActiveJobs = false;
        },
      });
  }

  loadMoreActiveJobs() {
    if (this.activeJobsHasMore && !this.loadingMoreActiveJobs) this.fetchActiveJobs(false);
  }
}
